import { useRef, useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";

import L from "leaflet";
import "leaflet-easybutton";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-easybutton/src/easy-button.css";
import { useTranslation } from "react-i18next";

import homeIcon from "../../../assets/images/checkout/home.png";

import { ArrowCounterClockwise, Target } from "@phosphor-icons/react";

const undoIconHTML = ReactDOMServer.renderToStaticMarkup(
  <ArrowCounterClockwise />,
);

const targetIconHTML = ReactDOMServer.renderToStaticMarkup(<Target />);

const STADIA_API_KEY = "739e5f98-582d-490c-bd09-78511fcb2cc2";

function MapLocationDrag(props) {
  const {
    center,
    firstCoordinates,
    setCoordinates,
    enabledMovePin,
    setEnabledMovePin,
  } = props;
  const { t } = useTranslation();
  const zoom = 17;
  const markerRef = useRef(null);
  const mapRef = useRef(null);
  const resetButton = useRef(null);

  const locationIcon = new L.Icon({
    iconUrl: homeIcon,
    iconSize: [44, 44],
    iconAnchor: [22, 44],
  });

  const [disabledButton, setDisabledButton] = useState(false);

  const eventHandlers = useMemo(() => ({
    dragstart() {
      resetButton.current.enable();
    },
    dragend() {
      const marker = markerRef.current;
      if (marker != null && marker.getLatLng() != null) {
        setDisabledButton(false);
      }
    },
  }));

  const pinButtonClick = () => {
    if (enabledMovePin) {
      const marker = markerRef.current;
      if (marker != null && marker.getLatLng() != null && !disabledButton) {
        setCoordinates("coordinates", [
          marker.getLatLng().lat,
          marker.getLatLng().lng,
        ]);
      }
    }
    setEnabledMovePin(!enabledMovePin);
  };

  const resetCoords = () => {
    if (firstCoordinates == null) return;
    mapRef.current.setView(firstCoordinates, mapRef.current.getZoom(), {
      animate: true,
      duration: 1,
    });
    setDisabledButton(false);
    setEnabledMovePin(false);
    setCoordinates("coordinates", firstCoordinates);
    markerRef.current.setLatLng(firstCoordinates);
    resetButton.current.disable();
  };

  const centerMap = () => {
    mapRef.current.setView(
      markerRef.current.getLatLng(),
      mapRef.current.getZoom(),
      {
        animate: true,
        duration: 1,
      },
    );
  };

  const createReset = () => {
    if (firstCoordinates != null && mapRef.current != null) {
      const centerLocationButton = L.easyButton(
        `<span class="bullseye">${targetIconHTML}</span>`,
        centerMap,
      );
      centerLocationButton.addTo(mapRef.current);

      resetButton.current = L.easyButton(
        `<span class="bullseye">${undoIconHTML}</span>`,
        resetCoords,
      );
      resetButton.current.addTo(mapRef.current);
      if (center[0] == firstCoordinates[0] && center[1] == firstCoordinates[1])
        resetButton.current.disable();
    }
  };

  const createExtras = (map) => {
    mapRef.current = map;
    createReset();
  };

  const displayMap = useMemo(
    () => (
      <MapContainer
        center={center}
        zoom={zoom}
        maxZoom={18}
        minZoom={8}
        whenCreated={createExtras}
        attributionControl={false}
      >
        <TileLayer
          url={`https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${STADIA_API_KEY}`}
          attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
        />
        <Marker
          draggable={enabledMovePin}
          autoPan={true}
          eventHandlers={eventHandlers}
          position={center}
          ref={markerRef}
          icon={locationIcon}
        ></Marker>
        <button
          disabled={disabledButton}
          className={`${
            disabledButton ? "disabled-button move-pin" : "move-pin"
          }`}
          onClick={pinButtonClick}
        >
          {enabledMovePin
            ? t("shipping.confirm-location")
            : t("shipping.move-pin")}
        </button>
      </MapContainer>
    ),
    [center, enabledMovePin, disabledButton],
  );

  return displayMap;
}

export default MapLocationDrag;
